import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { useUpdateMachineConfig } from '../hooks/useMachines'
import type { Machine, MachineConfig } from '../types'

interface MachineConfigSheetProps {
  machine: Machine | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MachineConfigSheet({
  machine,
  open,
  onOpenChange,
}: MachineConfigSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-lg"
        onOpenAutoFocus={(event) => event.preventDefault()}>
        {machine ? (
          // key remounts the form (re-initializing state from config) when the
          // selected machine changes — no effect needed to sync.
          <MachineConfigForm
            key={machine.machine_id}
            machine={machine}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function MachineConfigForm({
  machine,
  onClose,
}: {
  machine: Machine
  onClose: () => void
}) {
  const update = useUpdateMachineConfig()
  const config = machine.config ?? {}
  const initialEnabled = config.enabled ?? true
  const initialDryRun = config.dry_run ?? false
  const initialEventsEnabled = config.events_enabled ?? true
  const initialWorkflows = (config.workflows ?? []).join(', ')
  const initialPollInterval =
    config.poll_interval_s != null ? String(config.poll_interval_s) : ''

  const [enabled, setEnabled] = useState(initialEnabled)
  const [dryRun, setDryRun] = useState(initialDryRun)
  const [eventsEnabled, setEventsEnabled] = useState(initialEventsEnabled)
  const [workflows, setWorkflows] = useState(initialWorkflows)
  const [pollInterval, setPollInterval] = useState(initialPollInterval)

  const isDirty =
    enabled !== initialEnabled ||
    dryRun !== initialDryRun ||
    eventsEnabled !== initialEventsEnabled ||
    workflows !== initialWorkflows ||
    pollInterval !== initialPollInterval

  const handleSave = () => {
    const nextConfig: MachineConfig = {
      enabled,
      dry_run: dryRun,
      events_enabled: eventsEnabled,
      workflows: workflows
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    }
    const poll = Number(pollInterval)
    if (pollInterval.trim() && Number.isFinite(poll) && poll > 0) {
      nextConfig.poll_interval_s = poll
    }

    update.mutate(
      { machineId: machine.machine_id, config: nextConfig },
      {
        onSuccess: () => {
          toast.success('Machine config updated')
          onClose()
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Update failed'),
      }
    )
  }

  return (
    <>
      <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
        <SheetTitle className="truncate">{machine.machine_id}</SheetTitle>
        <SheetDescription>
          {machine.org} · {machine.env}
          {machine.updated_at
            ? ` · config updated ${formatDistanceToNow(new Date(machine.updated_at), { addSuffix: true })}`
            : null}
        </SheetDescription>
      </SheetHeader>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="machine-enabled">Enabled</Label>
          <Switch
            id="machine-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="machine-dry-run">Dry run</Label>
          <Switch
            id="machine-dry-run"
            checked={dryRun}
            onCheckedChange={setDryRun}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="machine-events">Events enabled</Label>
          <Switch
            id="machine-events"
            checked={eventsEnabled}
            onCheckedChange={setEventsEnabled}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="machine-workflows">
            Workflows (comma-separated; empty = any)
          </Label>
          <Input
            id="machine-workflows"
            value={workflows}
            onChange={(event) => setWorkflows(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="machine-poll">Poll interval (seconds)</Label>
          <Input
            id="machine-poll"
            value={pollInterval}
            onChange={(event) => setPollInterval(event.target.value)}
            inputMode="numeric"
          />
        </div>
        <div className="space-y-1">
          <Label>Environment (values hidden)</Label>
          {machine.env_keys.length > 0 ? (
            <pre className="max-h-48 overflow-auto rounded-md bg-muted p-2 text-xs">
              {machine.env_keys.map((key) => `${key}=***`).join('\n')}
            </pre>
          ) : (
            <p className="text-xs text-muted-foreground">
              No environment reported yet.
            </p>
          )}
        </div>
      </div>
      <SheetFooter>
        <Button onClick={handleSave} disabled={!isDirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </SheetFooter>
    </>
  )
}
